"use client";

import { MAX_HOMEWORK_PHOTO_BYTES } from "@/lib/homework-limits";

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.72;
/**
 * Camera encode: stay fast (toDataURL, not toBlob) but keep text/board readable.
 * ~1440px + q0.88 is a good compromise vs the old 960/0.72 mush.
 */
const CAMERA_MAX_EDGE = 1440;
const CAMERA_JPEG_QUALITY = 0.88;
/** Telegram Android fails large gallery FormData; skip re-encode when already compact. */
const SKIP_REENCODE_BYTES = 1_500_000;

function dataUrlToJpegBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) throw new Error("bad data url");
  const binary = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "image/jpeg" });
}

/**
 * Encode the current camera video frame to JPEG.
 * Never use canvas.toBlob here — on Telegram Android WebView it routinely takes 10–15s.
 * toDataURL stays fast enough at ~1440px on Telegram Android WebView.
 */
export async function captureHomeworkCameraFrame(video: HTMLVideoElement): Promise<File> {
  const srcW = video.videoWidth;
  const srcH = video.videoHeight;
  if (!srcW || !srcH) throw new Error("empty frame");

  const scale = Math.min(1, CAMERA_MAX_EDGE / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));
  const downscaling = scale < 1;

  let bitmap: ImageBitmap | null = null;
  try {
    if (typeof createImageBitmap === "function") {
      try {
        bitmap = await createImageBitmap(video, {
          resizeWidth: width,
          resizeHeight: height,
          resizeQuality: downscaling ? "medium" : "high",
        });
      } catch {
        bitmap = await createImageBitmap(video);
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = bitmap?.width ?? width;
    canvas.height = bitmap?.height ?? height;
    const ctx = canvas.getContext("2d", { alpha: false, willReadFrequently: false });
    if (!ctx) throw new Error("no canvas");
    // Smoothing helps when we still draw/scale; skip only if 1:1 blit of a resized bitmap.
    ctx.imageSmoothingEnabled = !(bitmap && bitmap.width === canvas.width && bitmap.height === canvas.height);
    if (ctx.imageSmoothingEnabled) ctx.imageSmoothingQuality = "medium";
    if (bitmap) ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    else ctx.drawImage(video, 0, 0, width, height);

    const blob = dataUrlToJpegBlob(canvas.toDataURL("image/jpeg", CAMERA_JPEG_QUALITY));
    if (blob.size > MAX_HOMEWORK_PHOTO_BYTES) {
      throw new Error("Одне фото має бути до 8 МБ.");
    }
    return new File([blob], `dz-${Date.now()}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    bitmap?.close();
  }
}

export function needsHomeworkNormalize(file: File): boolean {
  const type = file.type.toLowerCase();
  if (type === "image/jpeg" && file.name.startsWith("dz-")) return false;
  if ((type === "image/jpeg" || type === "image/jpg") && file.size > 0 && file.size <= SKIP_REENCODE_BYTES) {
    return false;
  }
  return true;
}

async function canvasToJpeg(
  source: CanvasImageSource,
  width: number,
  height: number,
): Promise<Blob> {
  const canOffscreen = typeof OffscreenCanvas !== "undefined";
  const canvas = canOffscreen
    ? new OffscreenCanvas(width, height)
    : Object.assign(document.createElement("canvas"), { width, height });

  const ctx = canvas.getContext("2d", { alpha: false }) as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error("Не вдалося підготувати фото.");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "medium";
  ctx.drawImage(source, 0, 0, width, height);

  let quality = JPEG_QUALITY;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const blob =
      "convertToBlob" in canvas
        ? await (canvas as OffscreenCanvas).convertToBlob({ type: "image/jpeg", quality })
        : await new Promise<Blob | null>((resolve) =>
            (canvas as HTMLCanvasElement).toBlob(resolve, "image/jpeg", quality),
          );
    if (!blob) break;
    if (blob.size <= MAX_HOMEWORK_PHOTO_BYTES) return blob;
    quality = Math.max(0.5, quality - 0.12);
  }
  throw new Error("Не вдалося стиснути фото.");
}

/** Re-encode gallery photos into a clean JPEG — Telegram Android often fails FormData with raw gallery files. */
export async function normalizeHomeworkPhoto(file: File): Promise<File> {
  if (!needsHomeworkNormalize(file)) return file;

  if (typeof createImageBitmap !== "function") {
    throw new Error("Не вдалося підготувати фото на цьому пристрої.");
  }

  // Prefer browser-side resize during decode when possible (much faster on Android WebView).
  const probe = await createImageBitmap(file);
  let bitmap = probe;
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(probe.width, probe.height));
    const width = Math.max(1, Math.round(probe.width * scale));
    const height = Math.max(1, Math.round(probe.height * scale));
    if (scale < 1) {
      try {
        bitmap = await createImageBitmap(probe, {
          resizeWidth: width,
          resizeHeight: height,
          resizeQuality: "low",
        });
        probe.close();
      } catch {
        bitmap = probe;
      }
    }
    const blob = await canvasToJpeg(bitmap, bitmap.width, bitmap.height);
    return new File([blob], `dz-${Date.now()}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    bitmap.close();
  }
}
